import { useCallback, useEffect, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import GlassCard from "Components/GlassCard/GlassCard";
import { StyledDivider } from "Components/UI/StyledDivider";
import SlidersSetup from "features/project/components/SlidersSetup";
import { useNotification, useSelectedProject } from "Hooks";
import {
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
  ApiProjectSettingKey,
} from "@incendium/api";
import { saveProjectSetting } from "features/project";
import AnalyticsCard from "features/analytics/components/AnalyticsCard";
import { useSave } from "Hooks/useSave";
import SavingButton from "Components/UI/SavingButton";

function AttributionSetupPage() {
  const [sliderValues, setSliderValues] = useState<number[]>([]);
  const [customSliderValues, setCustomSliderValues] = useState<number[]>([]);
  const { projectSettings, selectedProject } = useSelectedProject();
  const { save, saving } = useSave();
  const { showSuccessNotification, showErrorNotification } = useNotification();

  useEffect(() => {
    if (
      sliderValues.length === 0 &&
      projectSettings[ApiProjectSettingKey.EBA_ATTRIBUTION_MODEL]
    ) {
      setSliderValues(
        JSON.parse(projectSettings[ApiProjectSettingKey.EBA_ATTRIBUTION_MODEL]),
      );
    }
  }, [projectSettings, sliderValues]);

  useEffect(() => {
    if (
      customSliderValues.length === 0 &&
      projectSettings[ApiProjectSettingKey.DEFAULT_ATTRIBUTION_MODEL]
    ) {
      setCustomSliderValues(
        JSON.parse(
          projectSettings[ApiProjectSettingKey.DEFAULT_ATTRIBUTION_MODEL],
        ),
      );
    }
  }, [projectSettings, customSliderValues]);

  const onSaveEBA = useCallback(() => {
    save(async () => {
      try {
        await saveProjectSetting(
          {
            key: ApiProjectSettingKey.EBA_ATTRIBUTION_MODEL,
            value: JSON.stringify(sliderValues),
          },
          selectedProject!,
        );
        showSuccessNotification("EBA Model Saved");
      } catch (error) {
        showErrorNotification("Internal error saving EBA model");
      }
    });
  }, [
    selectedProject,
    sliderValues,
    save,
    showErrorNotification,
    showSuccessNotification,
  ]);

  const onSaveCustom = useCallback(() => {
    save(async () => {
      try {
        await saveProjectSetting(
          {
            key: ApiProjectSettingKey.DEFAULT_ATTRIBUTION_MODEL,
            value: JSON.stringify(customSliderValues),
          },
          selectedProject!,
        );
        showSuccessNotification("Custom Attribution Model Saved");
      } catch (error) {
        showErrorNotification("Internal error saving Custom Attribution model");
      }
    });
  }, [
    selectedProject,
    customSliderValues,
    save,
    showErrorNotification,
    showSuccessNotification,
  ]);

  return (
    <Stack spacing={4} mb={4}>
      <Stack spacing={2} direction="row" sx={{ height: 450 }}>
        <Box sx={{ display: "flex" }}>
          <GlassCard sx={{ height: "100%" }}>
            <Stack direction={"row"} justifyContent={"space-between"}>
              <Typography variant="subtitle1" mb={2}>
                EBA Setup
              </Typography>
              <SavingButton saving={saving} size="small" onClick={onSaveEBA}>
                Save Setup
              </SavingButton>
            </Stack>
            <StyledDivider />
            <SlidersSetup
              sliderValues={sliderValues}
              setSliderValues={setSliderValues}
            >
              <>
                <Typography variant="subtitle2" gutterBottom>
                  Attribute More Accurately & Transparently With EBA
                </Typography>
                <Typography variant="body2" gutterBottom>
                  EBA (Effectiveness-Based Attribution) is Incendium's
                  proprietary data-driven model for accurately assigning value
                  across the different touchpoints of a customer's journey to
                  sale.
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Using this tool, you can define the weightings for each part
                  of the journey. For example, if you think the top of the
                  funnel is more important, move the slider for the early
                  touchpoints up and the others down.
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Each slider represents a percentage of a user's journey to
                  sale from start to point of sale. For example, if you had 4
                  sliders, each would represent 25% of the journey, and if you
                  had 10, each would represent 10%.
                </Typography>
                <Typography variant="body2" gutterBottom>
                  We add the weight of that percentage to the number of
                  effective pageviews at that point in the journey.
                </Typography>
                <StyledDivider />
                <Typography variant="body2" mt={1}>
                  <b>Note</b>: changing these values does not have an immediate
                  impact. Our analytics will take some time to adjust, so could
                  be 30 minutes or more.
                </Typography>
              </>
            </SlidersSetup>
          </GlassCard>
        </Box>
      </Stack>
      <Stack spacing={2} direction="row" sx={{ height: 470 }}>
        <Box sx={{ display: "flex" }}>
          <GlassCard>
            <Stack direction={"row"} justifyContent={"space-between"}>
              <Typography variant="subtitle1" mb={2}>
                Custom Attribution Model Setup
              </Typography>
              <SavingButton saving={saving} size="small" onClick={onSaveCustom}>
                Save Setup
              </SavingButton>
            </Stack>
            <StyledDivider />
            <SlidersSetup
              sliderValues={customSliderValues}
              setSliderValues={setCustomSliderValues}
            >
              <>
                <Typography variant="subtitle2" gutterBottom>
                  Create Your Own Attribution Model
                </Typography>
                <Typography variant="body2" gutterBottom>
                  With our custom model builder, you have the power to design an
                  attribution model that aligns perfectly with your business
                  strategy. Rather than relying on a one-size-fits-all approach,
                  you can now adjust the weightings for each touchpoint along
                  your customer's journey.
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Using our the slider controls, assign a percentage value to
                  every stage from the first interaction to the final sale. For
                  instance, if you believe early touchpoints are critical,
                  simply increase the weight on the corresponding slider and
                  decrease it elsewhere. Each slider represents an equal portion
                  of the journey so if you're working with four stages, each
                  slider equals 25% of the total journey; if there are 10
                  stages, each represents 10%.
                </Typography>
                <Typography variant="body2" gutterBottom>
                  The weighted percentages are then applied to the sessions
                  along the journey, ensuring that the contribution of every
                  interaction is accurately reflected in your overall
                  attribution.
                </Typography>
                <StyledDivider />
                <Typography variant="body2" mt={1}>
                  <b>Note</b>: changing these values does not have an immediate
                  impact. Our analytics will take some time to adjust, so could
                  be 30 minutes or more.
                </Typography>
              </>
            </SlidersSetup>
          </GlassCard>
        </Box>
      </Stack>
      <Box sx={{ maxWidth: 450, height: 400 }}>
        <AnalyticsCard
          overrideDate={{
            lastNDays: 30,
            from: undefined,
            to: undefined,
          }}
          chart={{
            name: "Attribution Comparison (Last 30 Days)",
            description:
              "This shows the spread of conversions across top 10 sources. Toggle the attribution model to compare performance so you can adjust your model. Note: these are current model performances and will not be affected by changes to the sliders.",
            dimension: [ApiDimension.DIMENSION_SOURCE],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.PIE,
            attributes: [],
            limit: 10,
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT_EBA],
              },
            ],
            dateDimension: ApiDimension.DIMENSION_CONVERSION_BUCKET_DATE,
            displayOptions: {
              noFilters: true,
              showTotals: true,
            },
          }}
        />
      </Box>
    </Stack>
  );
}

export default AttributionSetupPage;
